import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { track } from "../utils/analytics";
import { consentPromptForOrder } from "../utils/consent";
import ArtworkCard from "./ArtworkCard";
import ConsentCard from "./ConsentCard";

export default function OrderOverview({ data, orderId, zipCode }) {
  const navigate = useNavigate();
  const { orderId: orderIdFromParams } = useParams();

  // Use orderId from params if available, otherwise use prop
  const currentOrderId = orderIdFromParams || orderId;

  // Scroll to top when OrderOverview is shown
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Track that user reached the order overview (successfully verified)
  useEffect(() => {
    track("Order Viewed", { order_id: currentOrderId });
  }, [currentOrderId]);

  // DEMO: the API has no `consent_prompt` field yet, so the partner is matched
  // off the product vendor client-side. See utils/consent.js.
  const consentPrompt = useMemo(
    () => consentPromptForOrder(data, currentOrderId),
    [data, currentOrderId],
  );

  const handleArtworkClick = (index) => {
    navigate(`/orders/${currentOrderId}/certificate?index=${index}`);
  };

  // Get fulfillment status and tracking info
  const fulfillments = data["fulfillments"] || [];
  const displayFulfillmentStatus = data["displayFulfillmentStatus"] || "UNFULFILLED";
  const hasTrackingInfo = fulfillments.some((f) => f.trackingInfo && f.trackingInfo.length > 0);

  // Get the most recent fulfillment for status determination
  const mostRecentFulfillment =
    fulfillments.length > 0
      ? [...fulfillments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
      : null;

  // Get the first available tracking info (number, carrier, URL)
  const getTrackingInfo = () => {
    for (const fulfillment of fulfillments) {
      if (fulfillment.trackingInfo && fulfillment.trackingInfo.length > 0) {
        for (const tracking of fulfillment.trackingInfo) {
          if (tracking.number) {
            return {
              number: tracking.number,
              company: tracking.company || null,
              url: tracking.url || null,
            };
          }
        }
      }
    }
    return null;
  };

  const trackingInfo = getTrackingInfo();

  // Get days since order was placed (returns 0 if unknown)
  const getDaysSinceOrder = () => {
    const orderDate = data["order_date"] ? new Date(data["order_date"]) : null;
    if (orderDate) {
      return Math.floor((new Date() - orderDate) / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const isUnfulfilled = fulfillments.length === 0 || displayFulfillmentStatus === "UNFULFILLED";

  // Determine user-friendly status message
  const getFriendlyStatus = () => {
    // If no fulfillments at all
    if (isUnfulfilled) {
      const days = getDaysSinceOrder();
      if (days >= 3) return "preparing for shipment";
      if (days >= 2) return "passing quality control";
      return "printing in progress";
    }

    // If partially fulfilled
    if (displayFulfillmentStatus === "PARTIAL") {
      return "some items have been shipped; others are at the studio";
    }

    // If we have fulfillments, check their status
    if (mostRecentFulfillment) {
      const status = mostRecentFulfillment.status;
      const inTransitAt = mostRecentFulfillment.inTransitAt;
      const estimatedDeliveryAt = mostRecentFulfillment.estimatedDeliveryAt;

      // Check if estimated delivery has passed (likely delivered)
      if (estimatedDeliveryAt) {
        const deliveryDate = new Date(estimatedDeliveryAt);
        const now = new Date();
        if (deliveryDate < now && status === "SUCCESS") {
          return "delivered";
        }
      }

      // If in transit
      if (inTransitAt || (status === "SUCCESS" && hasTrackingInfo)) {
        return "in transit";
      }

      // If fulfilled but not yet in transit
      if (status === "SUCCESS") {
        return "preparing for shipment";
      }

      // If pending
      if (status === "PENDING") {
        return "being prepared";
      }

      // If cancelled or failed
      if (status === "CANCELLED" || status === "FAILURE") {
        return "printing in progress";
      }
    }

    // Default fallback
    if (displayFulfillmentStatus === "FULFILLED") {
      return "being prepared";
    }

    return "printing in progress";
  };

  return (
    <div className="w-full md:w-1/2 h-full flex flex-col md:pt-24">
      <div className="flex items-end justify-between border-b border-solid border-oditi-black pb-4 animate-fade-in">
        <div>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tighter">
            Your Order
          </h2>
          <p className="font-mono uppercase text-xs text-oditi-gray mt-1">
            Order No. {currentOrderId} - {data["line_items"].length} Items Found
          </p>
        </div>
      </div>

      {/* Compact Status and Tracking Section */}
      <div className="mb-6 p-4 border-b bg-white border-solid border-oditi-black animate-fade-in">
        <div className="font-mono uppercase text-xs text-oditi-gray space-y-0.5">
          <p>status: {getFriendlyStatus()}</p>
          <p>
            tracking:{" "}
            {trackingInfo ? (
              trackingInfo.url ? (
                <a
                  href={trackingInfo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("Tracking Details Clicked", { order_id: currentOrderId })}
                  className="underline decoration-dotted underline-offset-4 hover:text-oditi-black"
                >
                  {trackingInfo.company
                    ? `${trackingInfo.company} (${trackingInfo.number}) →`
                    : `${trackingInfo.number} →`}
                </a>
              ) : (
                <span className="underline decoration-dotted underline-offset-4">
                  {trackingInfo.company
                    ? `${trackingInfo.company} (${trackingInfo.number})`
                    : trackingInfo.number}
                </span>
              )
            ) : isUnfulfilled ? (
              <span>available soon</span>
            ) : (
              <span>unavailable</span>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-12 pb-12">
        {data["line_items"].map((cert, idx) => {
          return (
            <ArtworkCard
              artwork={cert}
              key={idx}
              onClick={() => handleArtworkClick(idx)}
              printDate={data["print_date"]}
            />
          );
        })}
      </div>

      {/* Only rendered when the order carries a partner prompt — see ConsentCard. */}
      <ConsentCard consent={consentPrompt} orderId={currentOrderId} zipCode={zipCode} />
    </div>
  );
}
